import { useSearchParams } from 'react-router-dom';
import { Node, NodeAccessOperation, NodeAccessProfileStatus } from '@prisma/client';
import { Breadcrumb, Layout, Modal, Table } from 'antd';

import { ApprovalStage, ProfileWithDetails } from '@app/domain-t3-interfaces';
import { formatIsoDate } from '@app/utils-agnostic';
import { PageLayout, Tables } from '@app/web-components';
import { Common } from '@app/web-pages';

import { useFindNodes } from '../../admin/nodes';
import { ProfileOperationRenderer, ProfileStatusRenderer } from '../atoms';
import { useFindProfiles } from '../data';
import { useProfileActions } from '../libs';

export type LandingPageItem = ProfileWithDetails;

export const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const { data: nodeList } = useFindNodes<Node>({});
  const nodeName = searchParams.get('node');

  const approvalStages = {
    [ApprovalStage.MANAGER]: 'CW Sponsor',
    [ApprovalStage.EMS_ACCOUNT_MANAGER]: 'EMS Account Manager',
    [ApprovalStage.EXTERNAL_TSMC]: 'External TSMC',
  };

  const { getActionButton, getDeleteModalProps, getDetailsModalProps } = useProfileActions();
  const { getParentLayoutContentProps, getQuery, getTableProps, getToolbarProps } =
    Tables.useTableState<LandingPageItem>(
      'Profile',
      [
        {
          title: 'Action',
          width: 120,
          render: (_, record) => getActionButton(record),
        },
        {
          title: 'Status',
          dataIndex: 'status',
          width: 120,
          render: (status) => <ProfileStatusRenderer status={status} />,
          filters: [
            NodeAccessProfileStatus.PENDING,
            NodeAccessProfileStatus.APPROVED,
            NodeAccessProfileStatus.REJECTED,
          ].map((status) => ({
            text: status,
            value: status,
          })),
          filterSearch: true,
          sorter: true,
        },
        {
          title: 'Approval Stage',
          dataIndex: ['approvalStep', 'stage', 'name'],
          width: 150,
          filters: Object.entries(approvalStages).map(([key, value]) => ({
            text: value,
            value: key,
          })),
          render: (stage) => approvalStages[stage],
          filterSearch: true,
          ellipsis: true,
          sorter: true,
        },
        {
          title: 'Operation',
          dataIndex: 'operation',
          width: 120,
          render: (operation) => <ProfileOperationRenderer operation={operation} />,
          filters: [NodeAccessOperation.ADD, NodeAccessOperation.DELETE].map((status) => ({
            text: status,
            value: status,
          })),
          filterSearch: true,
          sorter: true,
        },
        {
          title: 'CW Name',
          dataIndex: ['user', 'name'],
          width: 200,
          ellipsis: true,
          sorter: true,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search names'),
        },
        {
          title: 'CW Wwid',
          dataIndex: ['user', 'enterpriseId'],
          render: (wwid: string) => <Common.WorkerLink enterpriseId={wwid} />,
          width: 120,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search wwids'),
        },
        {
          title: 'Project',
          dataIndex: ['die', 'name'],
          width: 200,
          ellipsis: true,
          sorter: true,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search projects'),
        },
        {
          title: 'Node',
          dataIndex: ['node', 'name'],
          width: 120,
          filters: nodeList?.data.map((node: Node) => ({
            text: node.name,
            value: node.name,
          })),
          filterSearch: true,
          defaultFilteredValue: nodeName ? [nodeName] : undefined,
          sorter: true,
        },
        {
          title: 'CW Sponsor',
          dataIndex: ['user', 'nextLevelEnterpriseId'],
          render: (wwid: string) => <Common.WorkerLink enterpriseId={wwid} />,
          width: 150,
          ellipsis: true,
          sorter: true,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search sponsor wwids'),
        },
        {
          title: 'Updated By',
          dataIndex: ['updatedByUser', 'name'],
          width: 150,
          ellipsis: true,
          sorter: true,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search updated by'),
        },
        {
          title: 'Updated On',
          dataIndex: 'updatedOn',
          width: 150,
          render: (date: string) => formatIsoDate(date),
          ellipsis: true,
          sorter: true,
        },
        {
          title: 'Created By',
          dataIndex: ['createdByUser', 'name'],
          width: 150,
          ellipsis: true,
          sorter: true,
          filterDropdown: Tables.customSearchFilterDropdownFactory('Search created by'),
        },
        {
          title: 'Created On',
          dataIndex: 'createdOn',
          width: 150,
          render: (date: string) => formatIsoDate(date),
          ellipsis: true,
          sorter: true,
        },
      ],
      {
        globalSearchFields: ['user.name', 'user.enterpriseId'],
      },
    );

  const queryResult = useFindProfiles<ProfileWithDetails>(
    getQuery({
      include: {
        user: true,
        die: true,
        node: true,
        supplierContract: true,
        approvalStep: {
          include: {
            stage: true,
          },
        },
        createdByUser: true,
        updatedByUser: true,
      },
    }),
  );

  return (
    <>
      <Modal {...getDeleteModalProps()} />
      <Modal {...getDetailsModalProps()} />
      <Breadcrumb style={{ margin: '16px 0' }} items={[{ title: 'Home' }, { title: 'Profiles' }]} />
      <Layout.Content {...getParentLayoutContentProps()}>
        <PageLayout.Title title="Profiles" />
        <div {...getToolbarProps({ queryResult })} />
        <Table {...getTableProps(queryResult)} />
      </Layout.Content>
    </>
  );
};
